"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";

import { LandingFoilMark } from "@/app/landing-v2/shared/LandingFoilMark";
import { BRAND } from "@/lib/brand";
import { cn } from "@/lib/utils";

type SiteFooterVariant = "full" | "compact";

interface SiteFooterProps {
  variant?: SiteFooterVariant;
  showCta?: boolean;
  className?: string;
}

const productLinks = [
  { label: "Find your niche", href: "/app" },
  { label: "Pricing", href: "/pricing" },
  { label: "See a sample", href: "/demo/niche/creator-deal-desk" },
  { label: "Blog", href: "/blog" },
];

const exploreLinks = [
  { label: "Startup ideas", href: "/ideas/ecommerce" },
  { label: "How to start", href: "/start/ai-writing-assistant" },
  { label: "Compare tools", href: "/compare/bolt-new" },
];

const companyLinks = [
  { label: BRAND.email, href: `mailto:${BRAND.email}` },
  { label: "X / Twitter", href: `https://x.com/${BRAND.twitterHandle}`, external: true },
  { label: "Instagram", href: `https://www.instagram.com/${BRAND.instagramHandle}/`, external: true },
];

const legalLinks = [
  { label: "Privacy", href: "/privacy" },
  { label: "Terms", href: "/terms" },
];

const compactLinks = [
  { label: "Find your niche", href: "/app" },
  { label: "Pricing", href: "/pricing" },
  ...legalLinks,
];

function FooterLink({
  href,
  label,
  external,
}: {
  href: string;
  label: string;
  external?: boolean;
}) {
  const className =
    "rounded-sm text-sm text-white/45 transition-colors hover:text-white/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950";

  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {label}
      </a>
    );
  }

  return (
    <Link href={href} className={className}>
      {label}
    </Link>
  );
}

function FooterColumn({
  title,
  links,
}: {
  title: string;
  links: Array<{ label: string; href: string; external?: boolean }>;
}) {
  return (
    <nav aria-label={`${title} footer links`}>
      <h3 className="font-heading text-[11px] font-semibold uppercase tracking-[0.28em] text-white/35">
        {title}
      </h3>
      <ul className="mt-4 space-y-2.5">
        {links.map((link) => (
          <li key={link.label}>
            <FooterLink {...link} />
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function SiteFooter({
  variant = "full",
  showCta = variant === "full",
  className,
}: SiteFooterProps) {
  const year = new Date().getFullYear();

  if (variant === "compact") {
    return (
      <footer
        role="contentinfo"
        className={cn(
          "@container border-t border-white/[0.08] bg-surface px-4 py-10 text-white",
          className,
        )}
      >
        <div className="mx-auto flex max-w-6xl flex-col gap-5 @3xl:flex-row @3xl:items-center @3xl:justify-between">
          <div>
            <div className="flex items-center gap-2 font-heading text-sm font-semibold text-white/55">
              <LandingFoilMark size="sm" animationDelay={0} />
              Made with {BRAND.name}
            </div>
            <p className="mt-2 max-w-md text-sm text-white/35">
              {BRAND.tagline} Foundry helps you find yours.
            </p>
          </div>

          <div className="flex flex-col gap-3 @3xl:items-end">
            <nav
              aria-label="Footer navigation"
              className="flex flex-wrap items-center gap-x-3 gap-y-2"
            >
              {compactLinks.map((link, index) => (
                <span key={link.label} className="inline-flex items-center gap-3">
                  <FooterLink {...link} />
                  {index < compactLinks.length - 1 && (
                    <span aria-hidden="true" className="text-white/20">
                      &middot;
                    </span>
                  )}
                </span>
              ))}
            </nav>
            <p className="text-xs text-white/25">
              &copy; {year} {BRAND.name}
            </p>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer
      role="contentinfo"
      className={cn("@container border-t border-white/[0.08] bg-surface text-white", className)}
    >
      {showCta && (
        <div className="border-b border-white/[0.08]">
          <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-12 @3xl:flex-row @3xl:items-center @3xl:justify-between">
            <div>
              <p className="font-heading text-[11px] font-semibold uppercase tracking-[0.32em] text-amber-300/65">
                Start here
              </p>
              <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-white @3xl:text-4xl">
                Find the place that fits.
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-6 text-white/45 @3xl:text-base">
                Answer four questions. Foundry finds the business idea that feels
                like it was waiting for you.
              </p>
            </div>
            <Link
              href="/app"
              className="inline-flex w-fit items-center gap-2 rounded-full bg-amber-400 px-5 py-3 font-heading text-sm font-semibold text-stone-950 shadow-[0_18px_48px_-18px_rgba(251,191,36,0.7)] transition hover:bg-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-200 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950"
            >
              Find your niche
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-6 py-12">
        <div className="grid gap-10 @3xl:grid-cols-[1.4fr_1fr_1fr_1fr_0.8fr]">
          <div>
            <Link
              href="/"
              className="inline-flex items-center gap-2.5 rounded-sm font-heading text-2xl font-bold text-white/75 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950"
            >
              <LandingFoilMark size="sm" animationDelay={0} />
              {BRAND.name}
            </Link>
            <p className="mt-4 max-w-sm text-sm leading-6 text-white/45">
              {BRAND.tagline} Foundry helps you find yours and build it.
            </p>
            <p className="mt-5 text-xs text-white/25">{BRAND.domain}</p>
          </div>

          <FooterColumn title="Product" links={productLinks} />
          <FooterColumn title="Explore" links={exploreLinks} />
          <FooterColumn title="Company" links={companyLinks} />
          <FooterColumn title="Legal" links={legalLinks} />
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-white/[0.08] pt-6 text-xs text-white/25 @3xl:flex-row @3xl:items-center @3xl:justify-between">
          <p>
            &copy; {year} {BRAND.name}. All rights reserved.
          </p>
          <p>Built for the moment before you know what to build.</p>
        </div>
      </div>
    </footer>
  );
}
